import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useDebouncedCallback } from 'use-debounce';

import styles from './physicalCards.module.scss';
import ToolbarFilter from './toolbarFilter';
import ImportModal from './physicalCards.import.modal';
import { config } from '../../api/config';
import { actions } from '../../state';
import {
  formatDate,
  formatMoney,
  generateHeader,
  isFilterActive,
  mapPhysicalCardBlocked,
  mapPhysicalCardBlockedColor,
  mapPhysicalCardOwnerType,
  mapPhysicalCardStatus,
  mapPhysicalCardStatusColor,
} from '../../utils';
import {
  Button,
  Header,
  HeaderActions,
  Icon,
  Table,
  TableCell,
  TableRow,
  Title,
} from '../../components';
import { getMembershipLabel } from './physicalCards.helpers';

const PAGE_SIZE = config.DEFAULTS.recordsPerPage;
const MIN_SEARCH_LENGTH = config.DEFAULTS.symbolsToSearch;
const defaultSortBody = {
  order: [{ by: 'id', order: 'desc' }],
};

const STATUS_OPTIONS = generateHeader([0, 1, 2, 3], mapPhysicalCardStatus);

const OWNER_TYPE_OPTIONS = generateHeader(
  [1351, 1352],
  mapPhysicalCardOwnerType
);

const tableHeader = [
  'Kortelės ID',
  'Statusas',
  'Likutis',
  'Savininko tipas',
  'Savininko ID',
  'Nuolaidos planas',
  'Išdavimo data',
  'Paskutinis papildymas',
  'Paskutinis naudojimas',
  'Blokuota',
];

const subtitle =
  'Visos plovyklų tinkle išduotos RFID kortelės — priskyrimas, likutis ir naudojimo istorija.';

const formatOptionalDate = value => (value ? formatDate(value) : '-');

const buildListFilter = (statusFilter, ownerTypeFilter) => {
  const filter = [];

  if (statusFilter.length) {
    filter.push({ field: 'status', value: statusFilter });
  }

  if (ownerTypeFilter.length) {
    filter.push({ field: 'owner_type', value: ownerTypeFilter });
  }

  return filter;
};

const PhysicalCards = ({
  dispatch,
  physicalCards: { physicalCards, total, loading },
}) => {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [statusFilter, setStatusFilter] = useState([]);
  const [ownerTypeFilter, setOwnerTypeFilter] = useState([]);
  const [appliedFilter, setAppliedFilter] = useState([]);
  const pageCount = Math.ceil(total / PAGE_SIZE) || 1;

  const fetchPhysicalCards = useCallback(
    data => {
      dispatch(actions.physicalCards.getPhysicalCardsAction(data));
    },
    [dispatch]
  );

  const searchPhysicalCards = useCallback(
    query => {
      dispatch(actions.physicalCards.searchPhysicalCardsAction(query));
    },
    [dispatch]
  );

  const clearPhysicalCards = useCallback(() => {
    dispatch(actions.physicalCards.clearPhysicalCardsAction());
  }, [dispatch]);

  const openImportModal = () => {
    dispatch(actions.physicalCards.clearPhysicalCardsImportAction());
    dispatch(
      actions.modals.setModalVisible({
        modalName: 'defaultModal',
        title: 'Masinis importas (CSV)',
        withScroll: true,
        children: <ImportModal />,
      })
    );
  };

  const handleDebouncedSearch = useDebouncedCallback(value => {
    const trimmed = value.trim();

    if (trimmed.length === 0) {
      setIsSearchMode(false);
      setPage(1);
      return;
    }

    if (trimmed.length >= MIN_SEARCH_LENGTH) {
      setIsSearchMode(true);
      setPage(1);
      searchPhysicalCards(trimmed);
    }
  }, 800);

  const handleSearchChange = ({ target: { value } }) => {
    setSearchInput(value);
    handleDebouncedSearch(value);
  };

  const handleFilterSubmit = () => {
    setAppliedFilter(buildListFilter(statusFilter, ownerTypeFilter));
    setPage(1);
  };

  useEffect(() => () => clearPhysicalCards(), [clearPhysicalCards]);

  useEffect(() => {
    if (isSearchMode) {
      return;
    }

    const offset = (page - 1) * PAGE_SIZE;
    fetchPhysicalCards({
      ...defaultSortBody,
      offset,
      ...(appliedFilter.length ? { filter: { and: appliedFilter } } : {}),
    });
  }, [fetchPhysicalCards, page, isSearchMode, appliedFilter]);

  useEffect(() => {
    if (isSearchMode) {
      return;
    }

    if (page > pageCount) {
      setPage(1);
    }
  }, [page, pageCount, isSearchMode]);

  const renderTable = () =>
    physicalCards.map(card => {
      const {
        id,
        number,
        status,
        current_balance,
        owner_type,
        owner_id,
        created_at,
        last_top_up_date,
        last_use_at,
        blocked,
      } = card;

      return (
        <TableRow url={config.APP_PATHS.editPhysicalCard(id)} key={id}>
          <TableCell>{number}</TableCell>
          <TableCell>
            <span style={{ color: mapPhysicalCardStatusColor(status) }}>
              {mapPhysicalCardStatus(status)}
            </span>
          </TableCell>
          <TableCell>{formatMoney(current_balance || 0)}</TableCell>
          <TableCell>{mapPhysicalCardOwnerType(owner_type)}</TableCell>
          <TableCell>{owner_id != null ? owner_id : '-'}</TableCell>
          <TableCell>{getMembershipLabel(card)}</TableCell>
          <TableCell>{formatOptionalDate(created_at)}</TableCell>
          <TableCell>{formatOptionalDate(last_top_up_date)}</TableCell>
          <TableCell>{formatOptionalDate(last_use_at)}</TableCell>
          <TableCell>
            <span style={{ color: mapPhysicalCardBlockedColor(blocked) }}>
              {mapPhysicalCardBlocked(blocked)}
            </span>
          </TableCell>
        </TableRow>
      );
    });

  return (
    <section>
      <Header>
        <div className={styles.titleGroup}>
          <Title total={total}>{config.PAGES.physicalCards}</Title>
          <p className={styles.subtitle}>{subtitle}</p>
        </div>

        <HeaderActions>
          <Button color="light" type="button" onClick={openImportModal}>
            Masinis importas (CSV)
          </Button>
          <Button color="primary" url={config.APP_PATHS.addPhysicalCard}>
            + Išduoti kortelę
          </Button>
        </HeaderActions>
      </Header>

      {/*
        BE GET /physical_cards/search matches against:
        card number, owner id (client_id / business_client_id),
        private client phone, and business client name.
      */}
      <div className={styles.toolbar}>
        <div className={styles.search}>
          <Icon name="search" classes={{ icon: styles.searchIcon }} />
          <input
            className={styles.searchInput}
            type="search"
            value={searchInput}
            onChange={handleSearchChange}
            autoComplete="off"
            placeholder="Paieška"
            aria-label="Paieška"
          />
        </div>

        <div className={styles.filters}>
          <div className={styles.filterTriggers}>
            <ToolbarFilter
              title="Statusas"
              options={STATUS_OPTIONS}
              valueType="number"
              filterState={statusFilter}
              setFilterState={setStatusFilter}
              onSubmit={handleFilterSubmit}
              isActive={isFilterActive('select', statusFilter)}
              disabled={isSearchMode}
            />

            <ToolbarFilter
              title="Savininko tipas"
              options={OWNER_TYPE_OPTIONS}
              valueType="number"
              filterState={ownerTypeFilter}
              setFilterState={setOwnerTypeFilter}
              onSubmit={handleFilterSubmit}
              isActive={isFilterActive('select', ownerTypeFilter)}
              disabled={isSearchMode}
            />
          </div>

          {isSearchMode && (
            <p className={styles.filterHint}>
              Išvalykite paiešką, kad filtruotumėte.
            </p>
          )}
        </div>
      </div>

      <Table
        header={tableHeader}
        isEmpty={!physicalCards.length}
        loading={loading}
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        serverPagination={!isSearchMode}
        onPageChange={setPage}
      >
        {renderTable()}
      </Table>
    </section>
  );
};

const mapStateToProps = state => ({
  physicalCards: state.physicalCards,
});

PhysicalCards.propTypes = {
  dispatch: PropTypes.func.isRequired,
  physicalCards: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    total: PropTypes.number.isRequired,
    physicalCards: PropTypes.array.isRequired,
  }).isRequired,
};

export default connect(mapStateToProps)(PhysicalCards);
